import { computed } from "vue";
import { isNullish } from "@tinyurldev/tu-ui-vue";
import { logError } from "@Helpers/logger";
import { getPercentage } from "@Helpers/support";
import useLegacyStore from "@Composables/useLegacyStore";

export default function useLimit(limitId) {
  const { getters } = useLegacyStore();

  const currentSubscription = computed(() => getters.getUserCurrentSubscription);
  const isUserLoggedIn = computed(() => getters.isUserLoggedIn);
  const userLimits = computed(() => getters.userLimits);
  const userOptions = computed(() => getters.userOptions);
  const linkWithUnlimitedClicksOption = computed(() => userOptions.value.links_with_unlimited_clicks);
  const maxAliasesPerMonthOption = computed(() => userOptions.value.max_aliases_per_month);

  const currentUsage = computed(() => {
    return getPercentage(usedCredits.value, maximumCredits.value, false);
  });

  const isCalculating = computed(() => {
    return userLimits.value?.calculating?.[limitId] === true;
  });

  const isWithinUnlimitedClicks = computed(() => {
    if (!currentSubscription.value?.isLegacy) {
      return false;
    }

    return usedCredits.value <= linkWithUnlimitedClicksOption.value;
  });

  const isBeyondUnlimitedClicks = computed(() => {
    if (!currentSubscription.value?.isLegacy) {
      return false;
    }

    return usedCredits.value >= linkWithUnlimitedClicksOption.value;
  });

  const isSoftLimitReached = computed(() => {
    return currentUsage.value >= 90;
  });

  const isHardLimitReached = computed(() => {
    return currentUsage.value >= 100;
  });

  const usedCredits = computed(() => {
    if (!isUserLoggedIn.value) {
      return 0;
    }

    return userLimits.value?.used[limitId];
  });

  const maximumCredits = computed(() => {
    if (!isUserLoggedIn.value) {
      return 0;
    }

    if (currentSubscription.value?.isLegacy) {
      if (usedCredits.value <= linkWithUnlimitedClicksOption.value) {
        return linkWithUnlimitedClicksOption.value;
      }

      return maxAliasesPerMonthOption.value;
    }

    return userLimits.value?.limit[limitId];
  });

  const usedOverage = computed(() => {
    const total = usedCredits.value - maximumCredits.value;

    return total > 0 ? total : 0;
  });

  const limitStatus = computed(() => userLimits.value?.status[limitId]);

  function hasStatus(givenStatus) {
    if (isNullish(limitStatus.value)) {
      logError(`[hasStatus]: No available status for limit: ${limitId}`);

      return false;
    }

    return (limitStatus.value & givenStatus) === givenStatus;
  }

  return {
    currentUsage,
    isCalculating,
    isWithinUnlimitedClicks,
    isBeyondUnlimitedClicks,
    isSoftLimitReached,
    isHardLimitReached,
    usedCredits,
    maximumCredits,
    usedOverage,
    limitStatus,
    hasStatus,
  };
}
